import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Download, Eye } from "lucide-react";
import type { FlexxiPlanStatus } from "@gaole/shared";
import { api, apiDownload, apiOpen, ApiError } from "../../lib/api";
import { fmtDateDMY, fmtDateTimeDMY, rm } from "../../lib/format";
import { useAuth } from "../../stores/auth";
import { toast } from "../../stores/toast";
import { Badge, PageLoader } from "../../components/ui";
import { ProductImage } from "../../components/ProductCard";
import { FlexxiPlanPanel } from "../../components/vault/FlexxiPlanPanel";
import { FlexxiProgressBar, FlexxiStatusBadge } from "../../components/flexxi/FlexxiPlanProgress";

type PaymentKind = "DEPOSIT" | "INSTALLMENT" | "LATE_FEE" | "ADJUSTMENT";

interface PlanDetail {
  plan: {
    id: string;
    status: FlexxiPlanStatus;
    statusLabel: string;
    principal: number;
    depositAmount: number;
    flexxiFeeAmount: number;
    interestTotal: number;
    totalPayable: number;
    paidTotal: number;
    outstanding: number;
    progressPercent: number;
    lateFeesAccrued: number;
    lateFeesPaid: number;
    productName: string;
    productImage: string | null;
    templateName: string;
    termMonths: number;
    upfrontMode: "DEPOSIT" | "FIRST_INSTALLMENT";
    createdAt: string;
    completedAt: string | null;
  };
  installments: {
    id: string;
    sequence: number;
    dueDate: string;
    amountDue: number;
    amountPaid: number;
    status: string;
    paidAt: string | null;
  }[];
  payments: {
    id: string;
    kind: PaymentKind;
    amount: number;
    createdAt: string;
    installmentSequence: number | null;
    note: string | null;
    orderId: string | null;
    orderNo: string | null;
    receiptAvailable: boolean;
  }[];
}

function paymentKindLabel(kind: PaymentKind, sequence: number | null, note: string | null): string {
  switch (kind) {
    case "DEPOSIT":
      return "Deposit";
    case "INSTALLMENT":
      return sequence ? `Instalment #${sequence}` : "Instalment";
    case "LATE_FEE":
      return "Late fee";
    case "ADJUSTMENT":
      return note ?? "Adjustment";
    default: {
      const exhaustive: never = kind;
      return exhaustive;
    }
  }
}

function installmentBadge(status: string, dueDate: string): { label: string; color: "green" | "amber" | "blue" | "gray" | "red" } {
  if (status === "PAID") return { label: "Paid", color: "green" };
  if (status === "WAIVED") return { label: "Waived", color: "gray" };
  if (status === "PARTIAL") return { label: "Partial", color: "amber" };
  if (new Date(dueDate) < new Date()) return { label: "Overdue", color: "red" };
  return { label: "Upcoming", color: "blue" };
}

export function FlexxiPlanDetail() {
  const { id = "" } = useParams();
  const { moduleEnabled } = useAuth();
  const receiptsOn = moduleEnabled("documents");

  const q = useQuery({
    queryKey: ["flexxi", "plan", id, "dashboard"],
    queryFn: () => api<PlanDetail>(`/flexxi/plans/${id}`),
    enabled: Boolean(id),
  });

  if (q.isLoading) return <PageLoader />;
  if (!q.data) {
    return (
      <div className="px-4 pt-6 text-sm text-white/60 lg:px-6">
        Plan not found.{" "}
        <Link to="/account/flexxi" className="text-brand-pink underline">
          Back to my plans
        </Link>
      </div>
    );
  }

  const { plan, installments, payments } = q.data;
  const paidCount = installments.filter((i) => i.status === "PAID" || i.status === "WAIVED").length;

  async function receipt(orderId: string, orderNo: string, mode: "view" | "download") {
    const path = `/orders/mine/${orderId}/documents/RECEIPT`;
    try {
      if (mode === "view") await apiOpen(path);
      else await apiDownload(path, `receipt-${orderNo}.pdf`);
    } catch (e) {
      toast("error", e instanceof ApiError ? e.message : "Couldn't load receipt");
    }
  }

  return (
    <div className="space-y-4 px-4 pb-8 pt-4 lg:px-6">
      <Link to="/account/flexxi" className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white">
        <ChevronLeft size={14} /> My FlexxiPlans
      </Link>

      <section className="glass-panel space-y-4 rounded-2xl p-4">
        <div className="flex gap-3">
          <div className="h-24 w-20 shrink-0 overflow-hidden rounded-xl bg-surface-2">
            <ProductImage src={plan.productImage} alt={plan.productName} className="h-full w-full" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-start justify-between gap-2">
              <h1 className="font-display text-base font-semibold">{plan.productName}</h1>
              <FlexxiStatusBadge status={plan.status} label={plan.statusLabel} />
            </div>
            <p className="text-xs text-white/50">
              {plan.templateName} · started {fmtDateDMY(plan.createdAt)}
              {plan.completedAt ? ` · paid off ${fmtDateDMY(plan.completedAt)}` : ""}
            </p>
            <p className="text-xs text-white/50">
              {plan.upfrontMode === "FIRST_INSTALLMENT"
                ? "No deposit — first instalment paid at checkout"
                : `Deposit paid at checkout: ${rm(plan.depositAmount)}`}
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between text-xs text-white/60">
            <span>
              {paidCount} of {installments.length} instalments paid
            </span>
            <span className="font-semibold text-white">{plan.progressPercent}%</span>
          </div>
          <FlexxiProgressBar percent={plan.progressPercent} />
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <Stat label="Total price" value={rm(plan.totalPayable)} />
          <Stat label="Paid" value={rm(plan.paidTotal)} />
          <Stat label="Outstanding" value={rm(plan.outstanding)} highlight />
        </div>
        {plan.lateFeesAccrued > 0 ? (
          <p className="text-xs text-amber-300">
            Late fees: {rm(plan.lateFeesPaid)} paid of {rm(plan.lateFeesAccrued)}
          </p>
        ) : null}
      </section>

      {plan.outstanding > 0 && plan.status !== "CANCELLED" && plan.status !== "DEFAULTED" ? (
        <FlexxiPlanPanel planId={plan.id} hideSchedule />
      ) : null}

      <section className="glass-panel rounded-2xl p-4">
        <h2 className="mb-2 font-display text-sm font-semibold">Instalment schedule</h2>
        <table className="w-full text-left text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-white/40">
            <tr>
              <th className="py-1.5 font-medium">#</th>
              <th className="py-1.5 font-medium">Due date</th>
              <th className="py-1.5 text-right font-medium">Amount</th>
              <th className="py-1.5 text-right font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {installments.map((i) => {
              const badge = installmentBadge(i.status, i.dueDate);
              return (
                <tr key={i.id} className="border-t border-white/5 text-white/75">
                  <td className="py-2 tabular-nums">{i.sequence}</td>
                  <td className="py-2 tabular-nums">
                    {fmtDateDMY(i.dueDate)}
                    {i.paidAt ? <span className="block text-[10px] text-white/40">Paid {fmtDateDMY(i.paidAt)}</span> : null}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {rm(i.amountDue)}
                    {i.status === "PARTIAL" ? (
                      <span className="block text-[10px] text-white/40">{rm(i.amountPaid)} paid</span>
                    ) : null}
                  </td>
                  <td className="py-2 text-right">
                    <Badge color={badge.color}>{badge.label}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="glass-panel rounded-2xl p-4">
        <h2 className="mb-2 font-display text-sm font-semibold">Payment history</h2>
        {payments.length === 0 ? (
          <p className="text-xs text-white/50">No payments recorded yet.</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {payments.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2.5 text-xs">
                <div className="min-w-0">
                  <p className="font-semibold text-white/85">
                    {paymentKindLabel(p.kind, p.installmentSequence, p.note)}
                  </p>
                  <p className="text-[11px] text-white/45">
                    {fmtDateTimeDMY(p.createdAt)}
                    {p.orderNo ? ` · ${p.orderNo}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="tabular-nums font-semibold">{rm(p.amount)}</span>
                  {receiptsOn && p.receiptAvailable && p.orderId && p.orderNo ? (
                    <>
                      <button
                        type="button"
                        className="rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white"
                        aria-label="View receipt"
                        title="View receipt"
                        onClick={() => void receipt(p.orderId!, p.orderNo!, "view")}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        type="button"
                        className="rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white"
                        aria-label="Download receipt"
                        title="Download receipt"
                        onClick={() => void receipt(p.orderId!, p.orderNo!, "download")}
                      >
                        <Download size={14} />
                      </button>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="rounded-xl bg-white/[0.04] p-2.5">
      <p className="text-[10px] uppercase tracking-wide text-white/45">{label}</p>
      <p className={`mt-0.5 text-sm font-bold tabular-nums ${highlight ? "text-brand-pink" : ""}`}>{value}</p>
    </div>
  );
}
